import { inject, Injectable } from '@angular/core';
import {
  BaseCommonClientRequestGroup,
  ClientRequestIdentityHelper,
  CommonClientRequestFactoryService,
  CommonClientRequestGroupJob,
  CommonClientRequestsState,
} from '@aiwyn/common/client-requests';
import { combineLatest, Observable, of } from 'rxjs';
import { ClientRequestFilters, ClientRequestFilterType } from '../models';
import { map, switchMap } from 'rxjs/operators';
import { combineLatestAsObject, ignoreNullish, isEmpty } from '@aiwyn/shared';
import { BaseServiceLine, ClientRequestGroupType, ClientRequestItemStatus } from '@aiwyn/api';
import { LaunchDarklyFlag, LaunchDarklyService } from '@aiwyn/config';

@Injectable({
  providedIn: 'root'
})
export class ClientRequestFactoryService extends CommonClientRequestFactoryService {
  private readonly launchDarklyService = inject(LaunchDarklyService);

  getAllFilteredClientRequestGroups$(filters$: Observable<ClientRequestFilters>): Observable<BaseCommonClientRequestGroup[]> {
    return combineLatest([
      this.allClientRequestGroups$.pipe(ignoreNullish()),
      this.launchDarklyService.getFeatureFlag(LaunchDarklyFlag.ENABLE_CLIENT_REQUESTS_UPDATED_FILTERS),
      this.store.select(CommonClientRequestsState.getAllClientRequests),
      filters$,
    ]).pipe(
      switchMap(([groups, areUpdatedFiltersEnabled, requests, filters]) => {
        if (isEmpty(filters)) {
          return of(groups);
        }

        return combineLatest(groups.map(group => {
          const isGroupJob = ClientRequestIdentityHelper.parseGroupIdentity(group.identity)?.type === ClientRequestGroupType.JOB;

          return combineLatestAsObject({
            identity: of(group.identity),
            client: group.typeClass$.pipe(switchMap(type => type.client$)),
            serviceLines: isGroupJob
              ? group.typeClass$.pipe(switchMap((type: CommonClientRequestGroupJob) => type.serviceLines$))
              : of([] as BaseServiceLine[]),
            status: group.typeClass$.pipe(switchMap(type => type.status$)),
          });
        })).pipe(
          // filter groups
          map(groupsData => groupsData.filter(group => {
            const targetClientIds = filters[ClientRequestFilterType.ACCOUNT];
            if (!isEmpty(targetClientIds)) {
              const clientId = ClientRequestIdentityHelper.parseClientIdentity(group.client.identity)?.id;
              const hasMatchingClient = targetClientIds.includes(clientId);
              if (!hasMatchingClient) {
                return false;
              }
            }

            const targetServiceLineIds = filters[ClientRequestFilterType.SERVICE];
            if (!isEmpty(targetServiceLineIds)) {
              const hasMatchingServiceLine = group.serviceLines.some(serviceLine => targetServiceLineIds.includes(serviceLine.id));
              if (!hasMatchingServiceLine) {
                return false;
              }
            }

            const targetProjectStatuses = filters[ClientRequestFilterType.STATUS];
            if (!isEmpty(targetProjectStatuses)) {
              const hasMatchingStatus = targetProjectStatuses.includes(group.status);
              if (!hasMatchingStatus) {
                return false;
              }
            }

            const targetItemStatuses = filters[ClientRequestFilterType.ITEM_STATUS] as ClientRequestItemStatus[];
            if (areUpdatedFiltersEnabled && !isEmpty(targetItemStatuses)) {
              const groupId = ClientRequestIdentityHelper.parseGroupIdentity(group.identity)?.id;
              const matchingRequests = requests.filter(request => request.jobId === groupId);
              const hasMatchingItemStatus = matchingRequests.some(request =>
                request.requestItems.some(item => targetItemStatuses.includes(item.status)),
              );

              if (!hasMatchingItemStatus) {
                return false;
              }
            }

            return true;
          })),

          // convert results back into matching groups
          map(groupsData => groupsData.map(({ identity }) => identity)),
          map(matchingIdentities => matchingIdentities.map(identity => groups.find(group => group.identity === identity))),
        );
      }),
    );
  }
}
